import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';

import type { ExportTemplateDefinition } from '../../types/export.types';

interface BrowseTemplateRadiosProps {
  templates: ExportTemplateDefinition[];
  defaultTemplateCode: string | null;
  /** The chosen code; `null` = the connector's default (raw) template. */
  template: string | null;
  onTemplateChange: (code: string | null) => void;
  /** Prefix for the radio ids, so two toolbars on one page never collide. */
  idPrefix?: string;
}

/**
 * The toolbar's template picker — compact radios with each description behind
 * an info tooltip. Shared by the batch browse page and the catalog export page
 * so both read the same; renders nothing when the connector has one template.
 */
export function BrowseTemplateRadios({
  templates,
  defaultTemplateCode,
  template,
  onTemplateChange,
  idPrefix = 'browse-template',
}: BrowseTemplateRadiosProps) {
  const { t } = useTranslation('exports');

  // Radios, not a select: there are two or three templates and the choice
  // changes what lands in the file, so every option stays visible — the same
  // reasoning as the drawer's.
  if (templates.length <= 1) return null;

  return (
    <fieldset className="flex items-center gap-2">
      <legend className="sr-only">{t('browse.template')}</legend>
      <RadioGroup
        value={template ?? '__default__'}
        onValueChange={(value) => {
          onTemplateChange(value === '__default__' ? null : value);
        }}
        className="flex flex-wrap items-center gap-x-4 gap-y-1"
      >
        {templates.map((option) => {
          const value = option.code === defaultTemplateCode ? '__default__' : option.code;
          const id = `${idPrefix}-${option.code}`;
          return (
            <span key={option.code} className="flex items-center gap-1.5">
              {/* `size-5`, not smaller: the indicator dot is a fixed 14px, so a
                  16px item leaves no ring around it. */}
              <RadioGroupItem value={value} id={id} className="size-5 cursor-pointer" />
              <Label htmlFor={id} className="cursor-pointer text-xs font-normal">
                {option.label}
              </Label>
              {/* The drawer shows each description inline; a toolbar has
                  no room, so it travels behind an info affordance. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground cursor-help"
                    aria-label={t('browse.templateDetailsAria', { template: option.label })}
                  >
                    <Info aria-hidden="true" className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm text-xs leading-relaxed">
                  {option.description}
                </TooltipContent>
              </Tooltip>
            </span>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
}
